import { useState, type FormEvent } from 'react';
import { submitReview, type PublishPreference } from '../utils/submitReview';
import type { ReviewerRelationship } from '../types/content';

export interface ReviewValues {
  name: string;
  email: string;
  title: string;
  company: string;
  relationship: ReviewerRelationship | '';
  experience: string;
  publishPreference: PublishPreference | '';
}

export type ReviewStatus = 'idle' | 'submitting' | 'success' | 'error' | 'not-configured';
export type ReviewErrors = Partial<Record<keyof ReviewValues, string>>;

const EMPTY: ReviewValues = { name: '', email: '', title: '', company: '', relationship: '', experience: '', publishPreference: '' };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(v: ReviewValues): ReviewErrors {
  const e: ReviewErrors = {};
  if (!v.name.trim()) e.name = 'Please enter your name.';
  if (!v.email.trim()) e.email = 'Please enter your email address.';
  else if (!EMAIL_PATTERN.test(v.email.trim())) e.email = 'Please enter a valid email address.';
  if (!v.relationship) e.relationship = 'Please tell us how you worked with us.';
  if (v.experience.trim().length < 30) e.experience = 'Please share a little more about your experience (at least 30 characters).';
  if (!v.publishPreference) e.publishPreference = 'Please choose how your feedback may be used.';
  return e;
}

export function useReviewForm() {
  const [values, setValues] = useState<ReviewValues>(EMPTY);
  const [errors, setErrors] = useState<ReviewErrors>({});
  const [status, setStatus] = useState<ReviewStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  function update<K extends keyof ReviewValues>(key: K, value: ReviewValues[K]) {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    if (status === 'error' || status === 'not-configured') setStatus('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = validate(values);
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) {
      document.getElementById(`review-${first}`)?.focus();
      return;
    }
    setStatus('submitting');
    const result = await submitReview({
      name: values.name.trim(),
      email: values.email.trim(),
      title: values.title.trim(),
      company: values.company.trim(),
      relationship: values.relationship as ReviewerRelationship,
      experience: values.experience.trim(),
      publishPreference: values.publishPreference as PublishPreference,
    });
    if (result.status === 'success') setStatus('success');
    else if (result.status === 'not-configured') setStatus('not-configured');
    else {
      setErrorMessage(result.message);
      setStatus('error');
    }
  }

  function reset() {
    setValues(EMPTY);
    setErrors({});
    setStatus('idle');
  }

  return { values, errors, status, errorMessage, update, handleSubmit, reset };
}
